import { computed } from 'vue';

import { useCartStore } from '@/shared/pinia/modules/cart';
import { computeShippingSubtotal } from '@/shared/utils/cart';

/**
 * Calculate shipping subtotal (excluding digital products and gift items)
 * This is used consistently across cart components to determine free shipping eligibility.
 *
 * Only includes physical products that:
 * - require shipping
 * - are not digital products (patterns or gift cards)
 * - are not gift items (items with _pickedReward property)
 *
 * Note: Cart-level discounts (coupons, order-level discounts) are NOT applied. This matches checkout behavior
 * where shipping thresholds are calculated based on pre-discount values.
 */
export function useCartShipping() {
  const cartStore = useCartStore();

  /**
   * Check if there are physical products in cart
   * Physical products are items that:
   * - require shipping
   * - are not digital products (patterns or gift cards)
   */
  const hasPhysicalProducts = computed(() => {
    if (!cartStore.cart?.items) {
      return false;
    }

    return cartStore.cart.items.some((item) => {
      const isGiftCard = item.gift_card || item.product?.gift_card;
      return item.requires_shipping && !isGiftCard;
    });
  });

  const shippingSubtotal = computed(() =>
    computeShippingSubtotal(cartStore.cart?.items ?? [])
  );

  const cartLevelDiscounts = computed(() => {
    const applications = cartStore.cart?.cart_level_discount_applications ?? [];
    return applications.map((d) => {
      const app = d.discount_application;
      return {
        title: app?.title ?? d.title ?? '',
        amount:
          app?.total_allocated_amount ??
          d.total_allocated_amount ??
          d.amount ??
          0
      };
    });
  });

  const totalCartLevelDiscounts = computed(() =>
    cartLevelDiscounts.value.reduce((sum, d) => sum + d.amount, 0)
  );

  return {
    shippingSubtotal,
    hasPhysicalProducts,
    cartLevelDiscounts,
    totalCartLevelDiscounts
  };
}
